/* eslint-disable camelcase */
/* eslint-disable no-case-declarations */
import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddCardIcon from "@mui/icons-material/AddCard"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../../../api"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import dayjs from "dayjs"
import Swal from "sweetalert2"

// Importación de los Tabs especializados
import TabTransferencia from "./TabTransferencia"
import TabTarjetaCredito from "./TabTarjetaCredito"
import TabRetenciones from "./TabRetenciones"

const TIPOS_PAGO = [
  { id: "EF", label: "Efectivo" },
  { id: "TR", label: "Transferencia Bancaria" },
  { id: "TA", label: "Tarjeta de Crédito / Débito" },
  { id: "CH", label: "Cheque a la Vista" },
  { id: "RI", label: "Retención IVA" },
  { id: "RF", label: "Retención en la Fuente" },
  { id: "NC", label: "Nota de Crédito" },
  { id: "AB", label: "Anticipo" },
  { id: "DG", label: "Documento en Garantía" },
]

const ModalFormasPago = ({ open, onClose, diferenciaCuadre, onAddPago, facturasCobrar }) => {
  // Estado del formulario de pago
  const [pago, setPago] = useState({
    tipo_pago: "EF",
    valor_real: "",
    banco_tarjeta: "",
    cuenta_referencia: "",
    cheque_transf: "",
    ttrcodigo: "",
    fecha: dayjs(),
    lote: "",
    autorizacion: "",
    adquiriente: "",
    propina: "",
    base_retencion: "",
    porcentaje_ret: "",
    serie_ret: "",
    secuencial_ret: "",
    facnumfac_aplicada: "",
  })

  // =========================================================================
  // PASO 2: EXTRACCIÓN DEFENSIVA EN REACT QUERY
  // =========================================================================
  const { data: rawCatalogos, isFetching } = useQuery({
    queryKey: ["catalogosCobro_Crear"],
    queryFn: async () => {
      try {
        const res = await api.post("/CobrosVista/getCatalogosCobro", {})
        // Desempaquetado seguro para objetos
        const extracted = res?.data?.data || res?.data || res || {}
        return extracted
      } catch (error) {
        console.error("Error al obtener catálogos", error)
        return {}
      }
    },
    enabled: open,
    refetchOnWindowFocus: false,
  })

  const cat = rawCatalogos || {}

  // Helper extremo para buscar arreglos sin importar el nivel de anidación (Axios suele envolver en data extra)
  const getSafeArray = (key) => {
    if (Array.isArray(cat[key])) return cat[key]
    if (cat.data && Array.isArray(cat.data[key])) return cat.data[key]
    return []
  }

  // Objeto de catálogos validado y 100% puro para los Combos (Evita "No options")
  const safeCat = {
    bancos_transferencia: getSafeArray("bancos_transferencia"),
    transacciones_cxp: getSafeArray("transacciones_cxp"),
    tarjetas: getSafeArray("tarjetas"),
    adquirientes: getSafeArray("adquirientes"),
    retencion_iva: getSafeArray("retencion_iva"),
    retencion_fuente: getSafeArray("retencion_fuente"),
  }

  // Efecto para autollenar al abrir el modal
  useEffect(() => {
    if (open) {
      setPago((prev) => ({
        ...prev,
        valor_real: diferenciaCuadre > 0 ? diferenciaCuadre.toFixed(2) : "",
        tipo_pago: "EF",
        fecha: dayjs(),
        facnumfac_aplicada: facturasCobrar?.[0]?.facnumfac || "",
      }))
    }
  }, [open, diferenciaCuadre, facturasCobrar])

  const handleInputChange = (field, value) => {
    setPago((prev) => ({ ...prev, [field]: value }))
  }

  // Lógica de cálculo automático para Retenciones
  useEffect(() => {
    if (pago.tipo_pago === "RI" || pago.tipo_pago === "RF") {
      const base = parseFloat(pago.base_retencion) || 0
      const porcentaje = parseFloat(pago.porcentaje_ret) || 0
      if (base > 0 && porcentaje > 0) {
        const valorCalculado = (base * (porcentaje / 100)).toFixed(2)
        setPago((prev) => ({ ...prev, valor_real: valorCalculado }))
      }
    }
  }, [pago.base_retencion, pago.porcentaje_ret, pago.tipo_pago])

  const handleAgregar = () => {
    const valorPagar = parseFloat(pago.valor_real) || 0

    // 1. Validaciones Generales
    if (valorPagar <= 0) {
      return Swal.fire("Aviso", "El valor del pago debe ser mayor a cero.", "warning")
    }

    if (valorPagar > diferenciaCuadre + 0.01) {
      return Swal.fire("Aviso", "El valor a pagar no puede ser mayor a la diferencia pendiente.", "warning")
    }

    // 2. Validaciones Específicas por Tipo de Pago
    if (pago.tipo_pago === "TR") {
      if (!pago.banco_tarjeta) return Swal.fire("Aviso", "Seleccione el Banco de la Compañía.", "warning")
      if (!pago.ttrcodigo) return Swal.fire("Aviso", "Seleccione el Tipo de Transacción.", "warning")
      if (!pago.cheque_transf) return Swal.fire("Aviso", "Ingrese el número de la transferencia.", "warning")
    }

    if (pago.tipo_pago === "TA") {
      if (!pago.banco_tarjeta) return Swal.fire("Aviso", "Seleccione la Tarjeta.", "warning")
      if (!pago.lote) return Swal.fire("Aviso", "Ingrese el Lote.", "warning")
      if (!pago.autorizacion) return Swal.fire("Aviso", "Ingrese la Autorización.", "warning")
      if (!pago.cheque_transf) return Swal.fire("Aviso", "Ingrese el número de Voucher.", "warning")

      const propinaTC = parseFloat(pago.propina) || 0
      if (valorPagar <= propinaTC && propinaTC > 0) {
        return Swal.fire(
          "Aviso",
          "El valor cobrado en la tarjeta no puede ser menor o igual que la propina.",
          "warning",
        )
      }
    }

    if (pago.tipo_pago === "RI" || pago.tipo_pago === "RF") {
      if (!pago.facnumfac_aplicada)
        return Swal.fire("Aviso", "Seleccione la factura a la que aplica la retención.", "warning")
      if (pago.serie_ret?.length !== 6)
        return Swal.fire("Aviso", "La serie del comprobante debe tener exactamente 6 dígitos (Ej. 001001).", "warning")
      if (pago.secuencial_ret?.length !== 9)
        return Swal.fire("Aviso", "El número secuencial debe tener 9 dígitos.", "warning")
      if (pago.autorizacion?.length !== 10 && pago.autorizacion?.length !== 49) {
        return Swal.fire("Aviso", "La Autorización SRI debe tener 10 o 49 dígitos.", "warning")
      }
      if (!pago.porcentaje_ret) return Swal.fire("Aviso", "Seleccione el porcentaje de retención.", "warning")
    }

    // 3. Formateo y Guardado
    const pagoFinal = {
      tipo_pago: pago.tipo_pago,
      tipo_pago_desc: TIPOS_PAGO.find((t) => t.id === pago.tipo_pago)?.label,
      valor_real: valorPagar,
      valor_transaccional: valorPagar,
      banco_tarjeta: pago.banco_tarjeta,
      cuenta_referencia: pago.cuenta_referencia || pago.autorizacion,
      cheque_transf: pago.tipo_pago === "RI" || pago.tipo_pago === "RF" ? pago.secuencial_ret : pago.cheque_transf,
      ttrcodigo: pago.tipo_pago === "RI" || pago.tipo_pago === "RF" ? pago.facnumfac_aplicada : pago.ttrcodigo,
      fecha_pago: pago.fecha ? dayjs(pago.fecha).format("YYYY-MM-DD") : null,
      facnumfac_aplicada: pago.facnumfac_aplicada,
      propina: pago.propina,
    }

    onAddPago(pagoFinal)
    onClose()
  }

  // Renderizadores Dinámicos pasando el safeCat
  const renderCamposDinamicos = () => {
    switch (pago.tipo_pago) {
      case "TR":
        return <TabTransferencia pago={pago} handleInputChange={handleInputChange} cat={safeCat} />
      case "TA":
        return <TabTarjetaCredito pago={pago} handleInputChange={handleInputChange} cat={safeCat} />
      case "RI":
      case "RF":
        const esIva = pago.tipo_pago === "RI"
        const opcionesRet = esIva ? safeCat.retencion_iva : safeCat.retencion_fuente
        return (
          <TabRetenciones
            pago={pago}
            handleInputChange={handleInputChange}
            facturasCobrar={facturasCobrar}
            opcionesRetencion={opcionesRet}
          />
        )
      default:
        return (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              Para esta forma de pago solo se requiere registrar el valor.
            </Typography>
          </Grid>
        )
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <CustomBackdrop isLoading={isFetching} />

      <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#f5f7fa", color: "#196C87" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold">
            Agregar Forma de Pago
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Seleccione la Forma de Pago *"
              value={pago.tipo_pago}
              onChange={(e) => {
                const nuevoTipo = e.target.value
                setPago({
                  ...pago,
                  tipo_pago: nuevoTipo,
                  valor_real: diferenciaCuadre > 0 ? diferenciaCuadre.toFixed(2) : "",
                  banco_tarjeta: "",
                  cuenta_referencia: "",
                  cheque_transf: "",
                  autorizacion: "",
                  lote: "",
                  propina: "",
                  base_retencion: "",
                  porcentaje_ret: "",
                  serie_ret: "",
                  secuencial_ret: "",
                  fecha: dayjs(),
                })
              }}
              sx={{ "& .MuiInputBase-root": { fontWeight: "bold", color: "#196C87" } }}
            >
              {TIPOS_PAGO.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.id} - {op.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Valor a Pagar *"
              type="number"
              value={pago.valor_real}
              onChange={(e) => handleInputChange("valor_real", e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                readOnly: pago.tipo_pago === "RI" || pago.tipo_pago === "RF",
              }}
              sx={{ "& .MuiInputBase-root": { fontWeight: "bold", color: "#d32f2f" } }}
            />
          </Grid>

          {renderCamposDinamicos()}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined" sx={{ fontWeight: "bold" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleAgregar}
          color="secondary"
          variant="contained"
          startIcon={<AddCardIcon />}
          sx={{ fontWeight: "bold" }}
        >
          Agregar Pago
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalFormasPago
